import { useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { isSubscriptionExpired } from '../utils/subscriptionGuard';

export const getAccessToken = () => {
    const auth = localStorage.getItem("AUTHDATA")
    if (auth) {
        return JSON.parse(auth).authToken;
    } else {
        return (process.env && process.env.DEFAULT_TOKEN) ?? undefined;
    }
}

export const getSessionAccessToken = () => {
    const auth = sessionStorage.getItem("AUTHDATA")
    if (auth) {
        return auth;
    }
}

export const downloadBlobFile = (response: any, defaultFilename: string = 'download', format?: string) => {
    const blob = new Blob([response.data], { 
        type: response.headers['content-type'] 
    });
    
    const contentDisposition = response.headers['content-disposition'];
    let filename = defaultFilename;
    
    if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (filenameMatch && filenameMatch[1]) {
            filename = filenameMatch[1].replace(/['"]/g, '');
        }
    } else if (format) {
        const extensionMap: Record<string, string> = {
            'excel': 'xlsx',
            'xlsx': 'xlsx',
            'pdf': 'pdf'
        };
        const extension = extensionMap[format.toLowerCase()] || format;
        const timestamp = new Date().toISOString().split('T')[0];
        filename = `${defaultFilename}_${timestamp}.${extension}`;
    }
    
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
};



// Razorpay checkout functions
declare global {
    interface Window {
        Razorpay: any;
    }
}

/**
 * Dynamically injects the Razorpay checkout script only when payment is needed.
 * This prevents Razorpay's tracking cookies from being set on every page load.
 */
const loadRazorpayScript = (): Promise<void> => {
    return new Promise((resolve, reject) => {
        if (window.Razorpay) {
            resolve();
            return;
        }
        const script = document.createElement('script');
        script.src = 'https://checkout.razorpay.com/v1/checkout.js';
        script.async = true;
        script.onload = () => resolve();
        script.onerror = () => reject(new Error('Failed to load Razorpay SDK'));
        document.head.appendChild(script);
    });
};

export const openRazorpayCheckout = async (subscriptionId: string) => {
    await loadRazorpayScript();

    const options = {
        key: getRazorpayKeyId(),
        subscription_id: subscriptionId,
        name: 'DriveMynt Subscription',
        description: 'Subscribe to our plan',
        handler: function (response: any) {
            window.location.href = `/subscription/success?subscriptionId=${subscriptionId}`;
        },
        prefill: {
            name: '',
            email: '',
            contact: ''
        },
        theme: {
            color: '#4f46e5'
        }
    };

    const rzp = new window.Razorpay(options);
    rzp.open();
};

export const openRazorpayOrderCheckout = async (orderId: string, amount: number) => {
    await loadRazorpayScript();

    const options = {
        key: getRazorpayKeyId(),
        order_id: orderId,
        amount: amount * 100,
        currency: 'INR',
        name: 'DriveMynt Subscription',
        description: 'Upgrade your subscription',
        handler: function (response: any) {
            window.location.href = `/subscription/success?orderId=${orderId}&paymentId=${response.razorpay_payment_id}`;
        },
        prefill: {
            name: '',
            email: '',
            contact: ''
        },
        theme: {
            color: '#4f46e5'
        }
    };

    const rzp = new window.Razorpay(options);
    rzp.open();
};

export interface PageAccess {
  isAllowed: boolean;
  isAdd: boolean;
  isEdit: boolean;
  isView: boolean;
  isCalendarView: boolean;
  isLocationTracking: boolean;
  isLocationSharing: boolean;
  isChatEnabled: boolean;
  isReadOnly: boolean;
}

const FULL_ACCESS: PageAccess = {
  isAllowed: true,
  isAdd: true,
  isEdit: true,
  isView: true,
  isCalendarView: true,
  isLocationTracking: true,
  isLocationSharing: true,
  isChatEnabled: true,
  isReadOnly: false,
};

const DEFAULT_ACCESS: PageAccess = {
  isAllowed: true,
  isAdd: true,
  isEdit: true,
  isView: true,
  isCalendarView: false,
  isLocationTracking: false,
  isLocationSharing: false,
  isChatEnabled: false,
  isReadOnly: false,
};

const PROFILE_SETTINGS_ROUTES = ['/profile', '/settings'];
const DASHBOARD_ONLY_ROUTES = ['/dashboard'];

export const getPageAccess = (route: string, reduxState?: any): PageAccess => {
  try {
    const auth = localStorage.getItem('AUTHDATA');
    const authData = auth ? JSON.parse(auth) : null;
    const role = authData?.role;
    if (role === 'SUPER_ADMIN') return { ...FULL_ACCESS };
    const readOnly = isSubscriptionExpired(reduxState?.userSubscriptionStatusReducer, role);
    const withReadOnly = (access: Omit<PageAccess, 'isReadOnly'>): PageAccess =>
      ({ ...access, isReadOnly: readOnly });

    if (!reduxState) return withReadOnly({ ...DEFAULT_ACCESS });
    const accessControlData = reduxState?.getAccessControlReducer?.data;
    const accessPoints: any[] = accessControlData?.accessPoints || [];

    if (!accessPoints.length) return withReadOnly({ ...DEFAULT_ACCESS });
    const normalizedRoute = route.startsWith('/') ? route : `/${route}`;
    const match = accessPoints.find((ap: any) => ap.route === normalizedRoute)
      || accessPoints.find((ap: any) => normalizedRoute.startsWith(ap.route));

    if (!match) return withReadOnly({ ...DEFAULT_ACCESS });

    return withReadOnly({
      isAllowed: match.isAllowed !== false,
      isAdd: match.isAdd !== false,
      isEdit: match.isEdit !== false,
      isView: match.isView !== false,
      isCalendarView: match.isCalendarView === true,
      isLocationTracking: match.isLocationTracking === true,
      isLocationSharing: match.isLocationSharing === true,
      isChatEnabled: match.isChatEnabled !== false,  // ← requires explicit true in access points
    });
  } catch {
    return { ...DEFAULT_ACCESS };
  }
};


export const useCurrentPageAccess = (): PageAccess => {
  const location = useLocation();
  const reduxState = useSelector((state: any) => state);
  const pathParts = location.pathname.split('/').filter(Boolean);
  const baseRoute = pathParts.length > 0 ? `/${pathParts[0]}` : '/';

  return getPageAccess(baseRoute, reduxState);
};

export const getTenantId = () => {
    const auth = sessionStorage.getItem("AUTHDATA") || localStorage.getItem("AUTHDATA");
    if (auth) {
        try {
            return JSON.parse(atob(auth)).tenantId;
        } catch {
            try {
                return JSON.parse(auth).tenantId;
            } catch {
                return undefined;
            }
        }
    }
}

export const getblobLink = () => {
    const auth = process.env.ENVIRONMENT ? process.env.ENVIRONMENT.toLowerCase() : 'dev';
    if (auth === 'prod') {
        return process.env.BLOB_LINK_PROD;
    } else {
        return process.env.BLOB_LINK_DEV;
    }
    
}

export const getRazorpayKeyId = () => {
    const env = process.env.ENVIRONMENT ? process.env.ENVIRONMENT.toLowerCase() : 'dev';
    if (env === 'prod') {
        return process.env.REACT_APP_RAZORPAY_KEY_ID_PROD;
    } else {
        return process.env.REACT_APP_RAZORPAY_KEY_ID_DEV;
    }   
}

